import { CandlestickChart, Combine, Sigma } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  CHAIN_MODES,
  CHAIN_MODE_ICON,
  CHAIN_MODE_LABEL,
  parseChainMode,
  type ChainMode,
} from './chainMode'

/**
 * Choosing which columns the ladder draws — ROADMAP 4.20.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * A STYLED LISTBOX, NOT A NATIVE `<select>`
 *
 * The native control renders in the operating system's chrome: white rows
 * and a blue highlight on this machine, whatever the theme is set to. On a
 * terminal that ships three themes, one of them a dark Bloomberg palette,
 * the one control that says which view you are in was the one element on
 * screen that ignored them.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * AN ITEM IS AN ICON AND A WORD, AND IT HAS TO BE
 *
 * Radix renders the SELECTED item's children into the trigger — that is what
 * `SelectValue` resolves to. So whatever an item contains, the closed
 * control contains too: the first cut gave each item a second line of
 * explanation and the trigger inherited it, stacking an icon over a label
 * over a wrapped sentence inside a 30px box.
 *
 * One line, one icon, one word. The explanation is on the trigger's
 * `title`, where it costs no layout and is still there for anyone who
 * wants it.
 *
 * The trigger therefore draws NO icon of its own. It would be a second
 * copy of the one the value already carries.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE TRIGGER IS DRESSED IN UTILITIES, NOT IN THE OLD `.tr-modepick`
 *
 * That stylesheet rule dressed a native `<select>` — `appearance: none`,
 * right padding for an arrow, and the arrow itself as two background
 * gradients. Radix draws its own chevron, so wearing it produced two of
 * them and 26px of padding holding a space where neither sat. It has gone
 * with the `<select>` it was written for; what is left here is the bar's
 * own metrics — 30px tall on the secondary surface, like the controls
 * beside it.
 * ─────────────────────────────────────────────────────────────────────────────
 */
const ICONS = {
  CandlestickChart,
  Combine,
  Sigma,
} as const

function ModeIcon({ mode }: { mode: ChainMode }) {
  const Icon = ICONS[CHAIN_MODE_ICON[mode]]
  return <Icon size={13} className="shrink-0 text-muted-foreground" />
}

export function ChainModePicker({
  value,
  onChange,
  title,
}: {
  value: ChainMode
  onChange: (next: ChainMode) => void
  /** What each view is for, and when the Greeks were last solved. */
  title: string
}) {
  return (
    <Select
      value={value}
      /*
       * Validated rather than cast. Radix types this as `string`, and the
       * value decides which columns the ladder draws — the one place a
       * stray value would be read as a layout rather than rejected.
       */
      onValueChange={(next) => onChange(parseChainMode(next, value))}
    >
      <SelectTrigger
        className="h-[30px] w-auto gap-1.5 rounded bg-secondary px-2.5 text-[12px] font-semibold"
        aria-label="Chain columns"
        data-testid="chain-mode"
        title={title}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent align="end" className="min-w-[9.5rem]">
        {CHAIN_MODES.map((m) => (
          <SelectItem key={m} value={m}>
            <span className="flex items-center gap-2 whitespace-nowrap">
              <ModeIcon mode={m} />
              {CHAIN_MODE_LABEL[m]}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
